import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import type { ValidationFilters, ValidationStats } from "@/types/validation";

export interface SourceFilterOption {
  id: string;
  label: string;
}

interface FilterBarProps {
  filters: ValidationFilters;
  onFiltersChange: (filters: ValidationFilters) => void;
  stats?: ValidationStats | undefined;
  /** Sources from the same route as Review (`GET /api/scrapers/sources`), plus any merged extras from the page. */
  sourceOptions: SourceFilterOption[];
  sourcesLoading?: boolean;
}

export function FilterBar({
  filters,
  onFiltersChange,
  stats,
  sourceOptions,
  sourcesLoading = false,
}: FilterBarProps) {
  const [searchInput, setSearchInput] = useState(filters.search || "");
  const [urlInput, setUrlInput] = useState(filters.url || "");

  /** Latest filters + callback so debounced effects never merge against a stale snapshot (would clear e.g. source). */
  const filtersRef = useRef(filters);
  const onFiltersChangeRef = useRef(onFiltersChange);
  filtersRef.current = filters;
  onFiltersChangeRef.current = onFiltersChange;

  // Sync URL input when parent sets url (e.g. "View" from By URL tab)
  useEffect(() => {
    if (filters.url !== undefined && filters.url !== urlInput) {
      setUrlInput(filters.url);
    }
  }, [filters.url]);

  // Debounce search input (message only)
  useEffect(() => {
    const timer = setTimeout(() => {
      const newFilters = { ...filtersRef.current };
      if (searchInput) {
        newFilters.search = searchInput;
      } else {
        delete newFilters.search;
      }
      onFiltersChangeRef.current(newFilters);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Debounce URL input
  useEffect(() => {
    const timer = setTimeout(() => {
      const newFilters = { ...filtersRef.current };
      const trimmed = urlInput.trim();
      if (trimmed) {
        newFilters.url = trimmed;
      } else {
        delete newFilters.url;
      }
      onFiltersChangeRef.current(newFilters);
    }, 300);
    return () => clearTimeout(timer);
  }, [urlInput]);

  const updateFilter = (
    key: keyof ValidationFilters,
    value: string | undefined,
  ) => {
    const newFilters: ValidationFilters = { ...filters };
    if (value) {
      // Type assertion needed due to union types
      if (
        key === "source" ||
        key === "level" ||
        key === "status" ||
        key === "validation_type" ||
        key === "scope" ||
        key === "url" ||
        key === "search" ||
        key === "product_id" ||
        key === "variant_id"
      ) {
        (newFilters as any)[key] = value;
      }
    } else {
      delete (newFilters as any)[key];
    }
    onFiltersChange(newFilters);
  };

  const clearFilters = () => {
    setSearchInput("");
    setUrlInput("");
    const newFilters: ValidationFilters = {};
    if (filters.limit) newFilters.limit = filters.limit;
    if (filters.offset) newFilters.offset = filters.offset;
    onFiltersChange(newFilters);
  };

  const activeFiltersCount = Object.keys(filters).filter(
    (key) =>
      key !== "limit" &&
      key !== "offset" &&
      filters[key as keyof ValidationFilters],
  ).length;

  const sourceOptionsWithSelection = (() => {
    if (!filters.source || sourceOptions.some((s) => s.id === filters.source)) {
      return sourceOptions;
    }
    return [{ id: filters.source, label: filters.source }, ...sourceOptions];
  })();

  const selectedSourceLabel =
    filters.source &&
    sourceOptionsWithSelection.find((s) => s.id === filters.source)?.label;

  // Get unique validation types from stats
  const validationTypes = stats ? Object.keys(stats.by_type) : [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="text-sm text-muted-foreground mb-1 block">
            Source
          </label>
          <Select
            value={filters.source || "all"}
            onValueChange={(v) =>
              updateFilter("source", v === "all" ? undefined : v)
            }
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  sourcesLoading ? "Loading sources…" : "All sources"
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {sourceOptionsWithSelection.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-1 block">
            Level
          </label>
          <Select
            value={filters.level || "all"}
            onValueChange={(v) =>
              updateFilter("level", v === "all" ? undefined : v)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="All levels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All levels</SelectItem>
              <SelectItem value="error">Errors only</SelectItem>
              <SelectItem value="warning">Warnings only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-1 block">
            Status
          </label>
          <Select
            value={filters.status || "all"}
            onValueChange={(v) =>
              updateFilter("status", v === "all" ? undefined : v)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="unresolved">Unresolved</SelectItem>
              <SelectItem value="addressed">Addressed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-1 block">
            Scope
          </label>
          <Select
            value={filters.scope || "all"}
            onValueChange={(v) =>
              updateFilter("scope", v === "all" ? undefined : v)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="All scopes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All scopes</SelectItem>
              <SelectItem value="product">Product</SelectItem>
              <SelectItem value="variant">Variant</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {validationTypes.length > 0 && (
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">
              Validation Type
            </label>
            <Select
              value={filters.validation_type || "all"}
              onValueChange={(v) =>
                updateFilter("validation_type", v === "all" ? undefined : v)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {validationTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type} ({stats!.by_type[type]})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div
          className={
            validationTypes.length > 0 ? "lg:col-span-2" : "lg:col-span-2"
          }
        >
          <label className="text-sm text-muted-foreground mb-1 block">
            URL
          </label>
          <Input
            placeholder="Filter by product or page URL..."
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
          />
        </div>
        <div
          className={
            validationTypes.length > 0 ? "lg:col-span-2" : "lg:col-span-2"
          }
        >
          <label className="text-sm text-muted-foreground mb-1 block">
            Search
          </label>
          <Input
            placeholder="Search by message..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      {activeFiltersCount > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          {filters.source && (
            <Badge variant="secondary" className="gap-1">
              Source: {selectedSourceLabel ?? filters.source}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => updateFilter("source", undefined)}
              />
            </Badge>
          )}
          {filters.level && (
            <Badge variant="secondary" className="gap-1">
              Level: {filters.level}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => updateFilter("level", undefined)}
              />
            </Badge>
          )}
          {filters.status && (
            <Badge variant="secondary" className="gap-1">
              Status: {filters.status}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => updateFilter("status", undefined)}
              />
            </Badge>
          )}
          {filters.scope && (
            <Badge variant="secondary" className="gap-1">
              Scope: {filters.scope}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => updateFilter("scope", undefined)}
              />
            </Badge>
          )}
          {filters.validation_type && (
            <Badge variant="secondary" className="gap-1">
              Type: {filters.validation_type}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => updateFilter("validation_type", undefined)}
              />
            </Badge>
          )}
          {filters.url && (
            <Badge variant="secondary" className="gap-1">
              URL:{" "}
              {filters.url.length > 50
                ? `${filters.url.slice(0, 47)}...`
                : filters.url}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => setUrlInput("")}
              />
            </Badge>
          )}
          {filters.search && (
            <Badge variant="secondary" className="gap-1">
              Search: {filters.search}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() => setSearchInput("")}
              />
            </Badge>
          )}
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear all
          </Button>
        </div>
      )}
    </div>
  );
}
